import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowUp, ArrowDown, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ResultStatusBadge } from './ResultStatusBadge';
import { PlayerLabel } from './PlayerLabel';
import { ResultButtons } from './ResultButtons';
import { VoidResultButton } from './VoidResultButton';
import type { Pairing } from '@swiss-magic/shared-types';
import type { GameConfig } from '@swiss-magic/shared-types';
import { effectiveMatchStatus } from '@swiss-magic/shared-types';

// Story 010 — derive the self-reporter name from a pairing. Returns
// null when the result was set by the director (or hasn't been set
// at all). The check matches reportedBy against each player's
// profileUserId — only player-set results show the badge.
function selfReportedByName(pairing: Pairing): string | null {
  if (!pairing.reportedBy) return null;
  const w = pairing.whitePlayer;
  if (w.profileUserId === pairing.reportedBy) {
    return `${w.firstName} ${w.lastName}`.trim();
  }
  const b = pairing.blackPlayer;
  if (b && b.profileUserId === pairing.reportedBy) {
    return `${b.firstName} ${b.lastName}`.trim();
  }
  return null;
}

export function PairingCard({
  pairing,
  index,
  roundId,
  roundStatus,
  isFirst,
  isLast,
  isPending,
  gameConfig,
  resultOptions,
  onSwap,
  onSetResult,
  onConfirmResult,
  onVoidResult,
  swapSelection,
  onPlayerSelect,
}: {
  pairing: Pairing;
  index: number;
  roundId: string;
  roundStatus: string;
  isFirst: boolean;
  isLast: boolean;
  isPending: 'result' | 'confirm' | null;
  gameConfig: GameConfig;
  resultOptions: { value: string; label: string }[];
  onSwap: (sourceIndex: number, targetIndex: number) => void;
  onSetResult: (roundId: string, pairingId: string, result: string) => void;
  onConfirmResult: (roundId: string, pairingId: string) => void;
  onVoidResult?: (roundId: string, pairingId: string) => void;
  swapSelection: string | null;
  onPlayerSelect?: (playerId: string) => void;
}) {
  const { t } = useTranslation('director');
  const isBye = !pairing.blackPlayer;
  const showResults = roundStatus === 'published' || roundStatus === 'completed';
  const isEditable = roundStatus === 'published' && !isBye;
  const isDraggable = roundStatus === 'paired';
  const selectable = roundStatus === 'paired' && !!onPlayerSelect;
  const showVoid =
    !!onVoidResult &&
    roundStatus === 'published' &&
    (pairing.resultStatus === 'pending' || pairing.resultStatus === 'confirmed');

  return (
    <div
      data-testid={`pairing-card-${pairing.tableNumber}`}
      className="rounded-lg border bg-card p-3"
    >
      {/* Header: table number + status */}
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span
            className="font-mono text-sm font-medium"
            data-testid={`table-number-${pairing.tableNumber}`}
          >
            {t('rounds_page.pairing.table_n', { tableNumber: pairing.tableNumber })}
          </span>
          {isDraggable && (
            <div className="flex gap-1">
              {!isFirst && (
                <button
                  className="h-6 w-6 rounded border text-muted-foreground hover:text-foreground"
                  data-testid={`move-up-${pairing.tableNumber}`}
                  onClick={() => onSwap(index, index - 1)}
                >
                  <ArrowUp className="mx-auto h-3 w-3" />
                </button>
              )}
              {!isLast && (
                <button
                  className="h-6 w-6 rounded border text-muted-foreground hover:text-foreground"
                  data-testid={`move-down-${pairing.tableNumber}`}
                  onClick={() => onSwap(index, index + 1)}
                >
                  <ArrowDown className="mx-auto h-3 w-3" />
                </button>
              )}
            </div>
          )}
        </div>
        {showResults && (
          <div className="flex flex-wrap items-center gap-1">
            <span data-testid={`result-status-${pairing.tableNumber}`}>
              <ResultStatusBadge status={effectiveMatchStatus(pairing)} startedAt={pairing.startedAt} />
            </span>
            {/* Story 010 — small "Self-reported by …" badge when the
                pairing's last setter is one of the two players (matched
                via profileUserId). Disappears as soon as the director
                overrides because reportedBy then points to them. */}
            {selfReportedByName(pairing) && (
              <Badge
                variant="outline"
                className="text-[10px] font-normal text-muted-foreground"
                data-testid={`self-reported-badge-${pairing.tableNumber}`}
              >
                {t('rounds_page.pairing.self_reported_by', { name: selfReportedByName(pairing) })}
              </Badge>
            )}
            {showVoid && (
              <VoidResultButton
                tableNumber={pairing.tableNumber}
                onClick={() => onVoidResult!(roundId, pairing.id)}
              />
            )}
          </div>
        )}
      </div>

      {/* Players */}
      <div className="mb-2 space-y-1">
        <div data-testid={`white-player-${pairing.tableNumber}`} className="text-sm">
          {gameConfig.hasColors && (
            <span className="mr-1 text-xs text-muted-foreground">
              {gameConfig.colorCodes.first.toUpperCase()}
            </span>
          )}
          <PlayerLabel
            player={pairing.whitePlayer}
            selectable={selectable}
            selected={swapSelection === pairing.whitePlayer.id}
            onSelect={onPlayerSelect}
            ratingClass="text-xs text-muted-foreground"
          />
        </div>
        <div data-testid={`black-player-${pairing.tableNumber}`} className="text-sm">
          {isBye ? (
            <span className="italic text-muted-foreground" data-testid="bye-indicator">
              {t('rounds_page.pairing.bye')}
            </span>
          ) : (
            <>
              {gameConfig.hasColors && (
                <span className="mr-1 text-xs text-muted-foreground">
                  {gameConfig.colorCodes.second.toUpperCase()}
                </span>
              )}
              <PlayerLabel
                player={pairing.blackPlayer!}
                selectable={selectable}
                selected={swapSelection === pairing.blackPlayer!.id}
                onSelect={onPlayerSelect}
                ratingClass="text-xs text-muted-foreground"
              />
            </>
          )}
        </div>
      </div>

      {/* Result entry / display */}
      {showResults && (
        <div>
          {isEditable ? (
            <div className="space-y-2">
              <div className="flex gap-1">
                <ResultButtons
                  tableNumber={pairing.tableNumber}
                  currentResult={pairing.result}
                  resultOptions={resultOptions}
                  pendingResult={isPending === 'result'}
                  onSetResult={(result) =>
                    onSetResult(roundId, pairing.id, result)
                  }
                  variant="card"
                />
              </div>
              {pairing.resultStatus === 'pending' && (
                <Button
                  size="sm"
                  variant="outline"
                  className={`w-full ${isPending === 'confirm' ? 'bg-amber-500 hover:bg-amber-600 text-white border-amber-500' : 'border-green-600 text-green-600 hover:bg-green-50'}`}
                  data-testid={`confirm-result-${pairing.tableNumber}`}
                  onClick={() => onConfirmResult(roundId, pairing.id)}
                >
                  <Check className="mr-1 h-4 w-4" />
                  {t('rounds_page.pairing.confirm')}
                </Button>
              )}
            </div>
          ) : (
            <span data-testid={`result-display-${pairing.tableNumber}`}>
              {isBye ? (
                <>
                  <Badge
                    variant="secondary"
                    className="text-xs"
                    data-testid={`result-status-${pairing.tableNumber}`}
                  >
                    {t('rounds_page.pairing.bye_result')}
                  </Badge>
                  {pairing.resultStatus === 'confirmed' && (
                    <span className="sr-only" data-testid={`bye-confirmed-${pairing.tableNumber}`}>
                      {t('rounds_page.pairing.bye_confirmed_sr')}
                    </span>
                  )}
                </>
              ) : (
                <span className="font-mono text-sm">
                  {pairing.result || '—'}
                </span>
              )}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
